import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button, FormHelperText, Grid, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { AsyncAutocomplete } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { CompetenceType } from '@/common';
import { getCompetenceTypes } from '../api/getCompetenceTypes';
import { FormType } from '../types';

type FormProps = {
  onSubmit: (data: Partial<FormType>) => void;
  initialState?: FormType;
  hasFormChanged?: (form: FormType) => boolean;
  onUnmount?: (form: FormType) => void;
  isFormEditable?: boolean;
};

const Form = ({ onSubmit, initialState, hasFormChanged, onUnmount, isFormEditable }: FormProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm<FormType>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      note: '',
      compType: null,
      validFrom: '',
      validTo: '',
      statusNote: '',
      confirmDat: '',
      CTStartDate: '',
      CTEndDate: '',
      CTEducationLevel: '',
      CTInstitutionName: '',
      CTAttainedTitle: '',
      CTCourseOrSeminarType: '',
      CTOrganizer: '',
      CTConfirmation: '',
      CTAttendType: '',
      CTLanguageName: '',
      CTLanguageListeningComprehension: 0,
      CTLanguageReadingComprehension: 0,
      CTLanguageSpeakingLevel: 0,
      CTLanguageWritingLevel: 0,
      CTOrganizationTitle: '',
      CTOrganizationLocation: '',
      CTOrganizationWebsite: '',
      CTTasks: '',
      CTStandardActivityClassification: '',
      CTPosition: '',
      CTProjectTitle: '',
      CTRole: '',
      CTCountry: '',
    },
  });

  useEffect(() => {
    return () => {
      if (onUnmount) {
        onUnmount(form.getValues());
      }

      form.reset({
        name: '',
        note: '',
        compType: null,
        statusNote: '',
        validFrom: '',
        validTo: '',
        confirmDat: '',
        CTStartDate: '',
        CTEndDate: '',
        CTEducationLevel: '',
        CTInstitutionName: '',
        CTAttainedTitle: '',
        CTCourseOrSeminarType: '',
        CTOrganizer: '',
        CTConfirmation: '',
        CTAttendType: '',
        CTLanguageName: '',
        CTLanguageListeningComprehension: undefined,
        CTLanguageReadingComprehension: undefined,
        CTLanguageSpeakingLevel: undefined,
        CTLanguageWritingLevel: undefined,
        CTOrganizationTitle: '',
        CTOrganizationLocation: '',
        CTOrganizationWebsite: '',
        CTTasks: '',
        CTStandardActivityClassification: '',
        CTPosition: '',
        CTProjectTitle: '',
        CTRole: '',
        CTCountry: '',
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.reset({
      name: initialState?.name,
      note: initialState?.note,
      compType: initialState?.compType || null,
      statusNote: initialState?.statusNote,
      validFrom: initialState?.validFrom
        ? new Date(initialState.validFrom).toLocaleDateString('en-CA')
        : undefined,
      validTo: initialState?.validTo
        ? new Date(initialState.validTo).toLocaleDateString('en-CA')
        : undefined,
      confirmDat: initialState?.confirmDat
        ? new Date(initialState.confirmDat).toLocaleDateString('en-CA')
        : '',
      CTStartDate: initialState?.CTStartDate
        ? new Date(initialState.CTStartDate).toLocaleDateString('en-CA')
        : '',
      CTEndDate: initialState?.CTEndDate
        ? new Date(initialState.CTEndDate).toLocaleDateString('en-CA')
        : '',
      CTEducationLevel: initialState?.CTEducationLevel,
      CTInstitutionName: initialState?.CTInstitutionName,
      CTAttainedTitle: initialState?.CTAttainedTitle,
      CTCourseOrSeminarType: initialState?.CTCourseOrSeminerType,
      CTOrganizer: initialState?.CTOrganizer,
      CTConfirmation: initialState?.CTConfirmation,
      CTAttendType: initialState?.CTAttendType,
      CTLanguageName: initialState?.CTLanguageName,
      CTLanguageListeningComprehension: initialState?.CTLanguageListeningComprehension,
      CTLanguageReadingComprehension: initialState?.CTLanguageReadingComprehension,
      CTLanguageSpeakingLevel: initialState?.CTLanguageSpeakingLevel,
      CTLanguageWritingLevel: initialState?.CTLanguageWritingLevel,
      CTOrganizationTitle: initialState?.CTOrganizationTitle,
      CTOrganizationLocation: initialState?.CTOrganizationLocation,
      CTOrganizationWebsite: initialState?.CTOrganizationWebsite,
      CTTasks: initialState?.CTTasks,
      CTStandardActivityClassification: initialState?.CTStandardActivityClassification,
      CTPosition: initialState?.CTPosition,
      CTProjectTitle: initialState?.CTProjectTitle,
      CTRole: initialState?.CTRole,
      CTCountry: initialState?.CTCountry,
    });
  }, [initialState, form]);

  const baseSubmit = (data: FormType) => {
    if (data.compType === null) {
      form.setError('compType', {
        type: 'validate',
        message: t('Competence type is required.', {
          defaultValue: 'Competence type is required.',
        }).toString(),
      });
      return;
    }
    if (hasFormChanged) {
      if (!hasFormChanged(data)) {
        enqueueSnackbar(t('Form was not changed.', { defaultValue: 'Form was not changed.' }), {
          variant: 'info',
        });
        return;
      }
    }

    onSubmit(data);
  };

  return (
    <Box component="form" onSubmit={form.handleSubmit(baseSubmit)} noValidate>
      {form.getValues('statusNote') && (
        <Box sx={{ mb: '2rem' }}>
          <Controller
            control={form.control}
            name="statusNote"
            render={({ field }) => {
              if (field.value) {
                return (
                  <TextField
                    {...field}
                    label={t('Status note', { defaultValue: 'Status note' })}
                    multiline
                    fullWidth
                    inputProps={{ disabled: true }}
                    color="error"
                    focused
                    error={!!form.formState.errors.statusNote}
                    helperText={form.formState.errors.statusNote?.message?.toString()}
                  />
                );
              }
              return <> </>;
            }}
          />
        </Box>
      )}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item md sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <AsyncAutocomplete<CompetenceType>
            id="comp-type"
            label={t('Type', { defaultValue: 'Type' })}
            fullWidth
            getData={async (search) => {
              const response = await getCompetenceTypes({
                page: 1,
                pageSize: 1000,
                search,
              });

              return response.results;
            }}
            getOptionLabel={(option) => `${option.typeShowAs} (${option.subTypeShowAs})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            selectedOption={form.getValues('compType')}
            error={!!form.formState.errors.compType}
            helperText={form.formState.errors.compType?.message?.toString()}
            setSelectedOption={(option) => {
              if (option === null) {
                form.setError('compType', {
                  type: 'validate',
                  message: t('Competence type is required.', {
                    defaultValue: 'Competence type is required. ',
                  }).toString(),
                });
                return;
              }

              form.setValue('compType', option, { shouldValidate: true, shouldDirty: true });
              form.clearErrors('compType');
            }}
            disabled={!isFormEditable}
          />

          <Controller
            control={form.control}
            name="name"
            rules={{
              required: t('Name is required.', { defaultValue: 'Name is required.' }).toString(),
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('Name', { defaultValue: 'Name' })}
                fullWidth
                error={!!form.formState.errors.name}
                helperText={form.formState.errors.name?.message?.toString()}
                disabled={!isFormEditable}
              />
            )}
          />

          <Controller
            control={form.control}
            name="note"
            rules={{
              required: t('Note is required.', { defaultValue: 'Note is required.' }).toString(),
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('Note', { defaultValue: 'Note' })}
                fullWidth
                multiline
                error={!!form.formState.errors.note}
                helperText={form.formState.errors.note?.message?.toString()}
                disabled={!isFormEditable}
              />
            )}
          />

          <Controller
            control={form.control}
            name="validFrom"
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  {...field}
                  slotProps={{
                    textField: {
                      error: false,
                    },
                  }}
                  value={dayjs(field.value)}
                  sx={{ width: '100%' }}
                  label={t('Valid from', { defaultValue: 'Valid from' })}
                  format="YYYY-MM-DD"
                  disabled={!isFormEditable}
                />
              </LocalizationProvider>
            )}
          />

          <Controller
            control={form.control}
            name="validTo"
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  {...field}
                  slotProps={{
                    textField: {
                      error: false,
                    },
                  }}
                  value={dayjs(field.value)}
                  sx={{ width: '100%' }}
                  label={t('Valid to', { defaultValue: 'Valid to' })}
                  format="YYYY-MM-DD"
                  disabled={!isFormEditable}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item md sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {form.getValues('compType')?.shownFields?.includes('CTStartDate') && (
            <Controller
              control={form.control}
              name="CTStartDate"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        error: false,
                      },
                    }}
                    value={dayjs(field.value)}
                    sx={{ width: '100%' }}
                    label={t('Start date', { defaultValue: 'Start date' })}
                    format="YYYY-MM-DD"
                    disabled={!isFormEditable}
                  />
                </LocalizationProvider>
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTEndDate') && (
            <Controller
              control={form.control}
              name="CTEndDate"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        error: false,
                      },
                    }}
                    value={dayjs(field.value)}
                    sx={{ width: '100%' }}
                    label={t('End date', { defaultValue: 'End date' })}
                    format="YYYY-MM-DD"
                    disabled={!isFormEditable}
                  />
                </LocalizationProvider>
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTEducationLevel') && (
            <Controller
              control={form.control}
              name="CTEducationLevel"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Education level', { defaultValue: 'Education level' })}
                  fullWidth
                  error={!!form.formState.errors.CTEducationLevel}
                  helperText={form.formState.errors.CTEducationLevel?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTInstitutionName') && (
            <Controller
              control={form.control}
              name="CTInstitutionName"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Institution name', { defaultValue: 'Institution name' })}
                  fullWidth
                  error={!!form.formState.errors.CTInstitutionName}
                  helperText={form.formState.errors.CTInstitutionName?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTAttainedTitle') && (
            <Controller
              control={form.control}
              name="CTAttainedTitle"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Attained title', { defaultValue: 'Attained title' })}
                  fullWidth
                  error={!!form.formState.errors.CTAttainedTitle}
                  helperText={form.formState.errors.CTAttainedTitle?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTCourseOrSeminarType') && (
            <Controller
              control={form.control}
              name="CTCourseOrSeminarType"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Course or seminer type', { defaultValue: 'Course or seminer type' })}
                  fullWidth
                  error={!!form.formState.errors.CTCourseOrSeminarType}
                  helperText={form.formState.errors.CTCourseOrSeminarType?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTOrganizer') && (
            <Controller
              control={form.control}
              name="CTOrganizer"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Organizer', { defaultValue: 'Organizer' })}
                  fullWidth
                  error={!!form.formState.errors.CTOrganizer}
                  helperText={form.formState.errors.CTOrganizer?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTConfirmation') && (
            <Controller
              control={form.control}
              name="CTConfirmation"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Confirmation num', { defaultValue: 'Confirmation' })}
                  fullWidth
                  error={!!form.formState.errors.CTConfirmation}
                  helperText={form.formState.errors.CTConfirmation?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTAttendType') && (
            <Controller
              control={form.control}
              name="CTAttendType"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Attend type', { defaultValue: 'Attend type' })}
                  fullWidth
                  error={!!form.formState.errors.CTAttendType}
                  helperText={form.formState.errors.CTAttendType?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTLanguageName') && (
            <Controller
              control={form.control}
              name="CTLanguageName"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Language name', { defaultValue: 'Language name' })}
                  fullWidth
                  error={!!form.formState.errors.CTLanguageName}
                  helperText={form.formState.errors.CTLanguageName?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form
            .getValues('compType')
            ?.shownFields?.includes('CTLanguageListeningComprehension') && (
            <Controller
              control={form.control}
              name="CTLanguageListeningComprehension"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Language listening comprehension', {
                    defaultValue: 'Language listening comprehension',
                  })}
                  type="number"
                  inputProps={{ step: 1 }}
                  fullWidth
                  error={!!form.formState.errors.CTLanguageListeningComprehension}
                  helperText={form.formState.errors.CTLanguageListeningComprehension?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTLanguageReadingComprehension') && (
            <Controller
              control={form.control}
              name="CTLanguageReadingComprehension"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Language reading comprehension', {
                    defaultValue: 'Language reading comprehension',
                  })}
                  type="number"
                  inputProps={{ step: 1 }}
                  fullWidth
                  error={!!form.formState.errors.CTLanguageReadingComprehension}
                  helperText={form.formState.errors.CTLanguageReadingComprehension?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTLanguageSpeakingLevel') && (
            <Controller
              control={form.control}
              name="CTLanguageSpeakingLevel"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Language speaking level', {
                    defaultValue: 'Language speaking level',
                  })}
                  type="number"
                  inputProps={{ step: 1 }}
                  fullWidth
                  error={!!form.formState.errors.CTLanguageSpeakingLevel}
                  helperText={form.formState.errors.CTLanguageSpeakingLevel?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTLanguageWritingLevel') && (
            <Controller
              control={form.control}
              name="CTLanguageWritingLevel"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Language writing level', { defaultValue: 'Language writing level' })}
                  fullWidth
                  type="number"
                  inputProps={{ step: 1 }}
                  error={!!form.formState.errors.CTLanguageWritingLevel}
                  helperText={form.formState.errors.CTLanguageWritingLevel?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTOrganizationTitle') && (
            <Controller
              control={form.control}
              name="CTOrganizationTitle"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Organization title', { defaultValue: 'Organization title' })}
                  fullWidth
                  error={!!form.formState.errors.CTOrganizationTitle}
                  helperText={form.formState.errors.CTOrganizationTitle?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTOrganizationLocation') && (
            <Controller
              control={form.control}
              name="CTOrganizationLocaiton"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Organization location', { defaultValue: 'Organization location' })}
                  fullWidth
                  error={!!form.formState.errors.CTOrganizationLocaiton}
                  helperText={form.formState.errors.CTOrganizationLocaiton?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTOrganizationWebsite') && (
            <Controller
              control={form.control}
              name="CTOrganizationWebsite"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Organization website', { defaultValue: 'Organization website' })}
                  fullWidth
                  error={!!form.formState.errors.CTOrganizationWebsite}
                  helperText={form.formState.errors.CTOrganizationWebsite?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTTasks') && (
            <Controller
              control={form.control}
              name="CTTasks"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Tasks', { defaultValue: 'Tasks' })}
                  fullWidth
                  error={!!form.formState.errors.CTTasks}
                  helperText={form.formState.errors.CTTasks?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form
            .getValues('compType')
            ?.shownFields?.includes('CTStandardActivityClassification') && (
            <Controller
              control={form.control}
              name="CTStandardActivityClassification"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Standard activity classification', {
                    defaultValue: 'Standard activity classification',
                  })}
                  fullWidth
                  error={!!form.formState.errors.CTStandardActivityClassification}
                  helperText={form.formState.errors.CTStandardActivityClassification?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTPosition') && (
            <Controller
              control={form.control}
              name="CTPosition"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Position', { defaultValue: 'Position' })}
                  fullWidth
                  error={!!form.formState.errors.CTPosition}
                  helperText={form.formState.errors.CTPosition?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTProjectTitle') && (
            <Controller
              control={form.control}
              name="CTProjectTitle"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Project title', { defaultValue: 'Project title' })}
                  fullWidth
                  error={!!form.formState.errors.CTProjectTitle}
                  helperText={form.formState.errors.CTProjectTitle?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTRole') && (
            <Controller
              control={form.control}
              name="CTRole"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Role', { defaultValue: 'Role' })}
                  fullWidth
                  error={!!form.formState.errors.CTRole}
                  helperText={form.formState.errors.CTRole?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}

          {form.getValues('compType')?.shownFields?.includes('CTCountry') && (
            <Controller
              control={form.control}
              name="CTCountry"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Country', { defaultValue: 'Country' })}
                  fullWidth
                  error={!!form.formState.errors.CTCountry}
                  helperText={form.formState.errors.CTCountry?.message?.toString()}
                  disabled={!isFormEditable}
                />
              )}
            />
          )}
        </Grid>
      </Grid>

      {isFormEditable && (
        <Button variant="contained" type="submit" fullWidth>
          {t('Submit', { defaultValue: 'Submit' })}
        </Button>
      )}

      {!!form.formState.errors.root && (
        <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
      )}
    </Box>
  );
};

export default Form;
